<template>
  <UserProfile>
    <UserProfileBlock
      :label="profile.label"
      :data="profile.data"
      :editable="isApplicationEditable"
      :on-edit="openEditModal"/>
    <div
      v-if="!isApplicationPending"
      class="onboarding-user-profile-block">
      <div class="onboarding-user-profile-header mb-5">
        <div class="onboarding-user-profile-header-title">
          Documents
        </div>
      </div>
      <UserDocumentsList
        :documents="personalDocs"
        :available-types="BUSINESS_ACTOR_DOCUMENTS"
        :hide-upload="isApplicationPending"
        :on-download="onDownload"
        :on-upload="uploadFile"/>
    </div>

    <PersonalDetailsForm
      v-if="isModalOpen.edit"
      v-model:isModalOpen="isModalOpen.edit"/>
  </UserProfile>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue';
import { useRoute } from 'vue-router';

import { BUSINESS_ACTOR_DOCUMENTS } from '@/constants';
import { getCountryName, getDate, transformAddress } from '@/modules/onboarding/utils';
import PersonalDetailsForm from '@/modules/onboarding-personal-profile/components/personal-details-form.vue';
import UserDocumentsList from '@/modules/onboarding-user-profile/components/user-documents-list.vue';
import UserProfile from '@/modules/onboarding-user-profile/components/user-profile.vue';
import UserProfileBlock from '@/modules/onboarding-user-profile/components/user-profile-block.vue';
import { OnboardingAdminRequests } from '@/services/requests';
import { Onboarding } from '@/services/requests/onboarding/Onboarding.types';
import { usePersonalOnboardingStore } from '@/stores/OnboardingPersonal';
import { CorporateDocumentType } from '@/types/interfaces/Document.interface';
import { errorNotification } from '@/utils';

const { personalDetails, applicationStatus, personalDocs } = usePersonalOnboardingStore();

const isModalOpen = reactive({
  edit: false,
});

function openEditModal() {
  isModalOpen.edit = true;
}

const profile = computed(() => ({
  label: 'Personal information',
  type: 'details',
  data: [
    {
      title: 'Nationality',
      value: getCountryName(personalDetails.value?.nationalityIso3),
    },
    {
      title: 'Country of residence',
      value: getCountryName(personalDetails.value?.countryOfResidenceIso3),
    },
    {
      title: 'Date of birth',
      value: getDate(personalDetails.value?.dateOfBirth),
    },
    {
      title: 'Address',
      value: personalDetails.value?.mailingAddress ? transformAddress(personalDetails.value?.mailingAddress) : '',
    },
  ],
}));

const { params: { id } } = useRoute();

const isApplicationEditable = computed(() => {
  const validStatuses: Onboarding.ApplicationStatus[] = ['NON_VERIFIED', 'VERIFIED'];
  if (!applicationStatus?.value) return false;
  return validStatuses.includes(applicationStatus.value);
});
const isApplicationPending = computed(() => applicationStatus?.value === 'PENDING');
async function onDownload(type: CorporateDocumentType): Promise<Blob | undefined> {
  const { response, error } = await OnboardingAdminRequests.Individual.getPersonalDocument(id, type);
  if (error) {
    errorNotification(error);
    return undefined;
  }

  return response;
}
const uploadFile = async (data: { type: CorporateDocumentType; formData: FormData }) => {
  await OnboardingAdminRequests.Individual.uploadPersonalDocument(id, data);
};
</script>
